"use client"

import { lazy, Suspense } from "react"

import { GlassCard } from "@shared/ui/landing/glass-card"
import { LiveBadge } from "@shared/ui/landing/status-tag"
import { SectionContainer } from "@shared/ui/landing/section-container"
import { StatCounter } from "@shared/ui/landing/stat-counter"

import { MAP_POINTS, getLiveStatFluctuation } from "@/lib/live-data"
import { useTranslations } from "@/lib/i18n/client"

const WorldPulseMap = lazy(() =>
  import("@shared/ui/landing/world-pulse-map").then((mod) => ({
    default: mod.WorldPulseMap,
  })),
)

export function WorldPulseSection() {
  const t = useTranslations("map")

  const metrics = [
    { value: getLiveStatFluctuation(842, 0.015), label: t("alerts"), suffix: "+" },
    { value: getLiveStatFluctuation(128, 0.02), label: t("corridors"), suffix: "+" },
    { value: getLiveStatFluctuation(5, 0.01), label: t("hubs"), suffix: "" },
  ] as const

  return (
    <SectionContainer id="map" className="bts-section-glow">
      <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div className="space-y-3">
          <LiveBadge label={t("liveLabel")} />
          <h2 className="text-3xl font-bold tracking-tight md:text-4xl">{t("title")}</h2>
          <p className="max-w-xl text-muted-foreground">{t("subtitle")}</p>
        </div>
        <div className="flex gap-4">
          {metrics.map((metric) => (
            <GlassCard key={metric.label} className="min-w-[100px] p-3 text-center">
              <p className="text-lg font-semibold tabular-nums">
                <StatCounter value={metric.value} suffix={metric.suffix ?? "+"} />
              </p>
              <p className="text-xs text-muted-foreground">{metric.label}</p>
            </GlassCard>
          ))}
        </div>
      </div>
      <GlassCard className="overflow-hidden p-2">
        <Suspense fallback={<div className="aspect-[2/1] animate-pulse rounded-2xl bg-white/5" />}>
          <WorldPulseMap points={[...MAP_POINTS]} />
        </Suspense>
        <div className="mt-4 flex flex-wrap justify-center gap-3 text-xs text-muted-foreground">
          {MAP_POINTS.map((point) => (
            <span key={point.name} className="inline-flex items-center gap-1.5">
              <span className="size-2 rounded-full bg-sky-400" />
              {point.name}
            </span>
          ))}
        </div>
      </GlassCard>
    </SectionContainer>
  )
}
